import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { BoxMockup } from "@/components/BoxMockup";
import { LabFormMockup } from "@/components/LabFormMockup";
import { Dialog } from "@/components/Dialog";
import { HelpTip } from "@/components/HelpTip";
import { Stepper } from "@/components/Stepper";
import { Button } from "@/components/Button";
import {
  ArrowLeftIcon,
  CheckIcon,
  InfoIcon,
  XIcon,
} from "@/components/icons";
import { useKit } from "@/api/useKit";
import { useBrandStore } from "@/state/brandStore";
import { useMyBrandProfile, usePlaceOrder } from "@/api/mutations";
import { ApiError } from "@/api/client";
import { cn } from "@/lib/cn";
import type { ReactNode } from "react";

/**
 * Approval — step 2 of 3. Per-artefact proof review with explicit ticks
 * on each item, a side-panel order summary, a compliance checklist, and a
 * final-confirmation dialog before the order is placed.
 *
 * The two artefacts are the outer kit box (3D) and the lab form (Phase 1
 * placeholder; Phase 3 reauthors as accurate SVG). Each artefact needs an
 * explicit tick before "Place order" enables.
 */
export function ApprovalPage() {
  const { kitId } = useParams();
  const navigate = useNavigate();
  const { data: kit } = useKit(kitId);
  const { brand } = useBrandStore();
  const { data: brandProfile } = useMyBrandProfile();
  const placeOrder = usePlaceOrder();

  const [boxApproved, setBoxApproved] = useState(false);
  const [formApproved, setFormApproved] = useState(false);
  const [zoom, setZoom] = useState<"box" | "form" | null>(null);
  const [showFinal, setShowFinal] = useState(false);
  const [placeError, setPlaceError] = useState<string | null>(null);

  const both = boxApproved && formApproved;

  const totals = useMemo(() => {
    if (!kit) return null;
    const qty = brand.quantity || 50;
    const unit =
      qty >= 500 ? kit.price * 0.88
      : qty >= 250 ? kit.price * 0.92
      : qty >= 100 ? kit.price * 0.95
      : kit.price;
    const subtotal = unit * qty;
    const setup = 35;
    const vat = (subtotal + setup) * 0.2;
    return { qty, unit, subtotal, setup, vat, total: subtotal + setup + vat };
  }, [kit, brand.quantity]);

  if (!kit || !totals) {
    return <div className="mx-auto max-w-7xl px-8 py-10 text-fg-3">Loading…</div>;
  }

  return (
    <div className="mx-auto max-w-[1480px] px-8 py-8">
      <Stepper steps={["Customise", "Review proof", "Order"]} current={1} complete={[0]} />

      <header className="mb-5 flex items-end justify-between gap-5">
        <div>
          <div className="mb-1 flex items-center gap-2 text-eyebrow font-semibold uppercase text-fg-mute">
            <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: "var(--studio)" }} />
            Step 2 of 3 · Approval
          </div>
          <h1 className="mb-0.5 text-xl font-semibold text-fg-1">
            Review &amp; approve every printed artefact
          </h1>
          <p className="m-0 max-w-2xl text-sm text-fg-3">
            This is what will print. Tick each item to confirm it's correct before you place the order.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate(`/catalogue/${kit.id}`)}
            leadingIcon={<ArrowLeftIcon />}
          >
            Back to customise
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-[1fr_360px] items-start gap-5">
        <div>
          <ProofItem
            title="1. Outer kit box"
            sub="Printed in full colour onto 350gsm white SBS board. 142 × 70 × 18 mm."
            approved={boxApproved}
            onApprove={setBoxApproved}
            onZoom={() => setZoom("box")}
          >
            <BoxMockup brand={brand} view="3d" size={0.85} />
          </ProofItem>

          <ProofItem
            title="2. Lab requisition form"
            sub="A5 duplex form with peel-off sample labels and prepaid return barcode."
            approved={formApproved}
            onApprove={setFormApproved}
            onZoom={() => setZoom("form")}
          >
            <LabFormMockup brand={brand} scale={0.62} />
          </ProofItem>

          <div className="overflow-hidden rounded-lg border border-[var(--border-1)] bg-white">
            <div className="flex items-center gap-1.5 border-b border-[var(--border-1)] bg-bg-1 px-4 py-3">
              <h3 className="m-0 text-sm font-semibold text-fg-1">Compliance checklist</h3>
              <HelpTip content="A read-only summary for your records — it shows that the locked clinical content is intact. This isn't a sign-off control; the per-artefact tickboxes above are what release the order to print." />
            </div>
            <div className="p-4">
              <ComplianceRow ok label="Lab return address preserved (UKAS scope)" />
              <ComplianceRow ok label="Kit code field unmodified" />
              <ComplianceRow ok label="Logo contrast on white: AA passing" />
              <ComplianceRow ok label="Patient field labels match SOP-FBC-04" />
              <ComplianceRow ok label="MHRA in vitro diagnostic markers intact" />
              <ComplianceRow ok label="Activation URL is HTTPS" />
            </div>
          </div>
        </div>

        {/* Order summary side */}
        <aside className="sticky top-5 rounded-lg border border-[var(--border-1)] bg-white p-5">
          <h3 className="mb-3 border-b border-[var(--border-1)] pb-3 text-sm font-semibold text-fg-1">
            Order summary
          </h3>
          <Line label="Kit" value={kit.title} />
          <Line label="Code" value={kit.code} mono />
          <Line label="Quantity" value={`${totals.qty} kits`} />
          <Line
            label="Unit price"
            value={`£${totals.unit.toFixed(2)}`}
            help="Tier discount applied automatically: 5% off at 100+ kits, 8% at 250+, 12% at 500+. Discounts apply per order — quantities don't combine across orders."
          />
          <div className="pt-2" />
          <Line label="Subtotal" value={`£${totals.subtotal.toFixed(2)}`} />
          <Line
            label="Print setup"
            value={`£${totals.setup.toFixed(2)}`}
            help="One-off cost per design that covers proof rendering and plate setup. Charged once per design, not per order — reorders of the same design skip this fee."
          />
          <Line label="VAT (20%)" value={`£${totals.vat.toFixed(2)}`} />
          <div className="mt-2 flex items-baseline justify-between border-t border-[var(--border-1)] pt-3 text-base font-bold">
            <span>Total</span>
            <span className="tabular-nums">£{totals.total.toFixed(2)}</span>
          </div>

          <hr className="my-4 border-0 border-t border-[var(--border-1)]" />

          <label className="mb-1 flex cursor-pointer items-center gap-2.5 py-2 text-sm">
            <input
              type="checkbox"
              checked={boxApproved}
              onChange={(e) => setBoxApproved(e.target.checked)}
              className="h-[18px] w-[18px] accent-[var(--studio)]"
            />
            <span>Outer box artwork approved</span>
          </label>
          <label className="mb-3 flex cursor-pointer items-center gap-2.5 py-2 text-sm">
            <input
              type="checkbox"
              checked={formApproved}
              onChange={(e) => setFormApproved(e.target.checked)}
              className="h-[18px] w-[18px] accent-[var(--studio)]"
            />
            <span>Lab form artwork approved</span>
          </label>

          <Button
            disabled={!both}
            onClick={() => setShowFinal(true)}
            leadingIcon={<CheckIcon />}
            className="h-12 w-full text-base bg-[var(--studio)] hover:bg-[var(--studio-dark)] active:bg-[var(--studio-dark)]"
          >
            Place order · £{totals.total.toFixed(2)}
          </Button>
          <p className="m-0 mt-3 text-center text-xxs text-fg-3">
            Estimated delivery 7–10 business days from approval.
          </p>
        </aside>
      </div>

      {/* Zoom dialog */}
      <Dialog
        open={zoom !== null}
        onOpenChange={(o) => !o && setZoom(null)}
        title="Artwork preview · actual print scale"
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => setZoom(null)}>
            Close
          </Button>
        }
      >
        <div className="grid min-h-[480px] place-items-center bg-bg-2 p-8">
          {zoom === "box" && <BoxMockup brand={brand} view="3d" size={1.2} />}
          {zoom === "form" && <LabFormMockup brand={brand} scale={1} />}
        </div>
      </Dialog>

      {/* Final confirmation dialog */}
      <Dialog
        open={showFinal}
        onOpenChange={setShowFinal}
        title="Final confirmation"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowFinal(false)}>
              Cancel
            </Button>
            <Button
              loading={placeOrder.isPending}
              disabled={!brandProfile}
              className="bg-[var(--studio)] hover:bg-[var(--studio-dark)] active:bg-[var(--studio-dark)]"
              onClick={async () => {
                if (!brandProfile || !kit) return;
                setPlaceError(null);
                try {
                  const order = await placeOrder.mutateAsync({
                    kitId: kit.id,
                    brandProfileId: brandProfile.id,
                    designName: `${kit.title} · ${brandProfile.name}`,
                    qty: brand.quantity || 50,
                    snapshot: {
                      brand: {
                        accent: brand.accent,
                        secondary: brand.secondary,
                        tagline: brand.tagline,
                        rhText: brand.rhText,
                        activationUrl: brand.activationUrl,
                        logoText: brand.logoText ?? null,
                        logoGcsPath: null,
                      },
                      content: { quantity: brand.quantity || 50, orgName: brand.orgName },
                    },
                  });
                  setShowFinal(false);
                  navigate(`/catalogue/${kit.id}/order?orderId=${encodeURIComponent(order.id)}`);
                } catch (err) {
                  setPlaceError(
                    err instanceof ApiError
                      ? `${err.status}: ${(err.body as { error?: string })?.error ?? err.message}`
                      : (err as Error).message,
                  );
                }
              }}
            >
              {placeOrder.isPending ? "Placing…" : "Confirm & place order"}
            </Button>
          </>
        }
      >
        <p className="mt-0">
          By placing this order you confirm{" "}
          <HelpTip content="You have a 60-minute window to cancel after placing the order, before production starts. After that, the approved proof becomes a contract artefact — it's hashed and kept on file as evidence of what was approved and by whom." />
          :
        </p>
        <ul className="list-disc pl-5 leading-relaxed text-fg-2">
          <li>The artwork shown is correct and matches your organisation's branding.</li>
          <li>You have permission to use the uploaded logo on diagnostic packaging.</li>
          <li>Once production starts (within 24 h), the design cannot be changed.</li>
          <li>Costs will be charged to your account: {brand.orgName} · Acct DEMO-001.</li>
        </ul>
        <div className="mt-4 flex gap-3 rounded-md border-l-[3px] border-[var(--brand-link)] bg-[#F3F9FC] p-3">
          <InfoIcon width={16} height={16} className="mt-0.5 flex-shrink-0 text-[var(--brand-link)]" />
          <div className="text-sm text-fg-2">
            Your account manager will be notified. A proof PDF will be emailed to{" "}
            <strong>practice@{brand.activationUrl.replace(/\/.*$/, "")}</strong>.
          </div>
        </div>
        {placeError && (
          <div className="mt-3 rounded-md border-l-[3px] border-[var(--danger)] bg-[#FFF8F7] p-3 text-sm text-[color:var(--danger-fg)]">
            Could not place order: {placeError}
          </div>
        )}
      </Dialog>
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────────────────── */

function ProofItem({
  title,
  sub,
  approved,
  onApprove,
  onZoom,
  children,
}: {
  title: string;
  sub: string;
  approved: boolean;
  onApprove: (v: boolean) => void;
  onZoom: () => void;
  children: ReactNode;
}) {
  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-[var(--border-1)] bg-white">
      <div className="flex items-center justify-between border-b border-[var(--border-1)] bg-bg-1 px-4 py-3">
        <div>
          <h3 className="m-0 text-sm font-semibold text-fg-1">{title}</h3>
          <div className="mt-0.5 text-xs text-fg-3">{sub}</div>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xxs font-semibold",
              approved ? "bg-[#E3F2EA] text-[color:var(--ok)]" : "bg-[#FBEFD8] text-[#6B4200]",
            )}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
            {approved ? "Approved" : "Awaiting approval"}
          </span>
          <Button variant="secondary" size="sm" onClick={onZoom}>
            Zoom
          </Button>
        </div>
      </div>
      <div className="grid min-h-[380px] place-items-center bg-bg-2 p-8">{children}</div>
      <div className="flex justify-between border-t border-[var(--border-1)] px-4 py-3">
        <label className="flex cursor-pointer items-center gap-2.5 text-sm">
          <input
            type="checkbox"
            checked={approved}
            onChange={(e) => onApprove(e.target.checked)}
            className="h-[18px] w-[18px] accent-[var(--studio)]"
          />
          <strong>I confirm this artwork is correct and ready to print</strong>
        </label>
      </div>
    </div>
  );
}

function ComplianceRow({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2.5 py-1.5 text-sm text-fg-1">
      <span
        className={cn(
          "grid h-[22px] w-[22px] flex-shrink-0 place-items-center rounded-full",
          ok ? "bg-[#E3F2EA] text-[color:var(--ok)]" : "bg-[#FAE3E1] text-[color:var(--danger)]",
        )}
      >
        {ok ? <CheckIcon width={12} height={12} strokeWidth={2.5} /> : <XIcon width={12} height={12} strokeWidth={2.5} />}
      </span>
      <span>{label}</span>
    </div>
  );
}

function Line({
  label,
  value,
  mono,
  help,
}: {
  label: string;
  value: string;
  mono?: boolean;
  help?: string;
}) {
  return (
    <div className="flex items-baseline justify-between py-1 text-sm">
      <span className="inline-flex items-center gap-1.5 text-fg-3">
        <span>{label}</span>
        {help && <HelpTip content={help} />}
      </span>
      <span className={cn("font-medium tabular-nums text-fg-1", mono && "font-mono")}>
        {value}
      </span>
    </div>
  );
}
